import * as React from "react";
import { 
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  Tab, 
  Tabs, 
  Box, 
  Typography, 
  IconButton, 
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Divider
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Swal from "sweetalert2";
import { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../../../../api";
import ShopForm from "./ShopForm";

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`shop-tabpanel-${index}`}
      aria-labelledby={`shop-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const initialShopState = {
  name: "",
  phone: "",
  aboutUs: "",
  address: "",
  location: "",
  instagramLink: "",
  websiteLink: "",
  tiktokkLink: "",
  facebookLink: "",
  youtubeLink: "",
  logo: null,
  banner: null,
  otherImages: [null, null, null, null],
  image: null
};

const getApi = () => {
  const token = localStorage.getItem("token");
  return axios.create({
    baseURL: API_URL(),
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
};

export default function ShopDialog({ open, close, getShops, editData, hasShop }) {
  const api = getApi();
  const [maxBranches, setMaxBranches] = useState(5);

  const [tabValue, setTabValue] = useState(0);
  const [mainShop, setMainShop] = useState(initialShopState);
  const [branches, setBranches] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const fetchSettings = async () => {
      try {
        const res = await getApi().get("admin-setting");
        const list = res.data.data;
        if (list && list.length > 0) {
          setMaxBranches(list[0].minimunShop ?? 5);
        }
      } catch (err) {
        if (err.response?.status !== 404) {
          console.error("Failed to fetch admin settings:", err);
        }
      }
    };
    fetchSettings();
  }, [open]);

  // Branch Form View State
  const [isBranchFormOpen, setIsBranchFormOpen] = useState(false);
  const [currentBranch, setCurrentBranch] = useState(initialShopState);
  const [editingBranchIndex, setEditingBranchIndex] = useState(null);

  useEffect(() => {
    if (editData) {
      setMainShop({
        name: editData.name || "",
        phone: editData.phone || "",
        aboutUs: editData.aboutUs || "",
        address: editData.address || "",
        location: (editData.latitude && editData.longitude)
          ? `${editData.latitude}, ${editData.longitude}`
          : (editData.location && Array.isArray(editData.location) && editData.location.length >= 2)
          ? `${editData.location[0]}, ${editData.location[1]}`
          : "",
        instagramLink: editData.instagramLink || "",
        websiteLink: editData.websiteLink || "",
        tiktokkLink: editData.tiktokkLink || "",
        facebookLink: editData.facebookLink || "",
        youtubeLink: editData.youtubeLink || "",
        logo: editData.logo || null,
        banner: editData.banner || null,
        otherImages: editData.otherImages || [null, null, null, null],
        image: editData.image || null
      });

      const formattedBranches = (editData.branches || []).map(branch => ({
        ...branch,
        location: (branch.latitude && branch.longitude)
          ? `${branch.latitude}, ${branch.longitude}`
          : (branch.location && Array.isArray(branch.location) && branch.location.length >= 2)
          ? `${branch.location[0]}, ${branch.location[1]}`
          : ""
      }));
      setBranches(formattedBranches);
    } else {
      resetForm();
    }
  }, [editData, open]);

  const resetForm = () => {
    setMainShop(initialShopState);
    setBranches([]);
    setErrors({});
    setTabValue(0);
    setIsBranchFormOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setIsBranchFormOpen(false); // Close branch form if they switch tabs
  };

  const validate = (data) => {
    let tempErrors = {};
    if (!data.name) tempErrors.name = "Shop Name is required";
    if (!data.phone) tempErrors.phone = "Phone is required";
    if (!data.address) tempErrors.address = "Address is required";
    if (data.location) {
      const parts = data.location.split(',');
      if (parts.length !== 2 || isNaN(parts[0].trim()) || isNaN(parts[1].trim()) || parts[0].trim() === "" || parts[1].trim() === "") {
        tempErrors.location = "Invalid format. Must be 'Latitude, Longitude' (e.g. 16.8409, 96.1735)";
      } else {
        const lat = parseFloat(parts[0].trim());
        const lng = parseFloat(parts[1].trim());
        if (lat < -90 || lat > 90) {
          tempErrors.location = "Invalid Latitude. Must be between -90 and 90.";
        } else if (lng < -180 || lng > 180) {
          tempErrors.location = "Invalid Longitude. Must be between -180 and 180.";
        }
      }
    }
    return tempErrors;
  };

  // Branch Management
  const openAddBranch = () => {
    setCurrentBranch(initialShopState);
    setEditingBranchIndex(null);
    setErrors({});
    setIsBranchFormOpen(true);
  };

  const openEditBranch = (index) => {
    setCurrentBranch(branches[index]);
    setEditingBranchIndex(index);
    setErrors({});
    setIsBranchFormOpen(true);
  };

  const saveBranch = () => {
    const branchErrors = validate(currentBranch);
    if (Object.keys(branchErrors).length > 0) {
      setErrors(branchErrors);
      Swal.fire("Validation Error", "Please check the highlighted fields for errors", "warning");
      return;
    }

    setErrors({});

    if (editingBranchIndex !== null) {
      const updatedBranches = [...branches];
      updatedBranches[editingBranchIndex] = currentBranch;
      setBranches(updatedBranches);
    } else {
      setBranches([...branches, currentBranch]);
    }
    setIsBranchFormOpen(false);
  };

  const removeBranch = (index) => {
    const updatedBranches = branches.filter((_, i) => i !== index);
    setBranches(updatedBranches);
  };

  const handleSubmit = async () => {
    const mainErrors = validate(mainShop);
    if (Object.keys(mainErrors).length > 0) {
      setErrors(mainErrors);
      setTabValue(0);
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", mainShop.name || "");
      formData.append("phone", mainShop.phone || "");
      formData.append("aboutUs", mainShop.aboutUs || "");
      formData.append("address", mainShop.address || "");
      if (mainShop.location) {
        const [lat, lng] = mainShop.location.split(',');
        formData.append("latitude", lat.trim());
        formData.append("longitude", lng.trim());
      }
      formData.append("instagramLink", mainShop.instagramLink || "");
      formData.append("websiteLink", mainShop.websiteLink || "");
      formData.append("tiktokkLink", mainShop.tiktokkLink || "");
      formData.append("facebookLink", mainShop.facebookLink || "");
      formData.append("youtubeLink", mainShop.youtubeLink || "");

      if (mainShop.logo instanceof File) {
        formData.append("logo", mainShop.logo);
      } else if (mainShop.logo === null) {
        formData.append("logo", "");
      }

      if (mainShop.banner instanceof File) {
        formData.append("banner", mainShop.banner);
      } else if (mainShop.banner === null) {
        formData.append("banner", "");
      }

      if (mainShop.otherImages && mainShop.otherImages.length > 0) {
        mainShop.otherImages.forEach((img) => {
          if (img instanceof File) {
            formData.append("otherImages", img);
          }
        });
      }

      if (branches && branches.length > 0) {
        branches.forEach((branch, index) => {
          formData.append(`branches[${index}][name]`, branch.name || "");
          formData.append(`branches[${index}][phone]`, branch.phone || "");
          formData.append(`branches[${index}][aboutUs]`, branch.aboutUs || "");
          formData.append(`branches[${index}][address]`, branch.address || "");

          if (branch.location) {
            const [lat, lng] = branch.location.split(',');
            formData.append(`branches[${index}][latitude]`, lat.trim());
            formData.append(`branches[${index}][longitude]`, lng.trim());
          }

          if (branch.image instanceof File) {
            formData.append(`branches[${index}][image]`, branch.image);
          } else if (branch.image === null) {
            formData.append(`branches[${index}][image]`, "");
          }
        });
      }

      const axiosConfig = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };

      if (editData) {
        await api.patch(`v2/shop/${editData._id || editData.id}`, formData, axiosConfig);
        Swal.fire("Success", "Shop updated successfully", "success");
      } else {
        await api.post("v2/shop", formData, axiosConfig);
        Swal.fire("Success", "Shop created successfully", "success");
      }
      getShops();
      close();
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Something went wrong", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={close}
      fullWidth 
      maxWidth="md"
      scroll="paper"
    >
      <DialogTitle component="div" sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", bgcolor: "#f8f9fa" }}>
        <Typography variant="h6" fontWeight="bold">
          {editData ? "Edit Shop" : "Add New Shop"}
        </Typography>
        <IconButton onClick={close} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, bgcolor: "#f8f9fa" }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="shop tabs">
          <Tab label="Main Shop Details" id="shop-tab-0" disabled={isBranchFormOpen} />
          <Tab label={`Branches (${branches.length})`} id="shop-tab-1" />
        </Tabs>
      </Box>

      <DialogContent dividers sx={{ p: 0 }}>
        <TabPanel value={tabValue} index={0}>
          <ShopForm 
            formData={mainShop} 
            setFormData={setMainShop} 
            errors={errors} 
          />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          {isBranchFormOpen ? (
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
                <IconButton size="small" onClick={() => setIsBranchFormOpen(false)} sx={{ color: "#555" }}>
                  <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6" color="primary">
                  {editingBranchIndex !== null ? "Edit Branch" : "Add New Branch"}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <ShopForm 
                formData={currentBranch} 
                setFormData={setCurrentBranch} 
                errors={errors} 
                isBranch={true}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
                <Button onClick={() => setIsBranchFormOpen(false)}>Cancel</Button>
                <Button onClick={saveBranch} variant="contained" color="primary">
                  Save Branch
                </Button>
              </Box>
            </Box>
          ) : (
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="500">Shop Branches</Typography>
                <Button 
                  variant="outlined" 
                  startIcon={<AddIcon />} 
                  onClick={openAddBranch}
                  size="small"
                  disabled={branches.length >= maxBranches}
                >
                  {branches.length >= maxBranches ? "Branch Limit Reached" : "Add Branch"}
                </Button>
              </Box>

              {branches.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: "center", bgcolor: "#fafafa", border: "1px dashed #ccc" }}>
                  <Typography color="text.secondary">No branches added yet. Click "Add Branch" to create one.</Typography>
                </Paper>
              ) : (
                <List sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 1, border: "1px solid #eee" }}>
                  {branches.map((branch, index) => (
                    <React.Fragment key={index}>
                      <ListItem>
                        <ListItemText
                          primary={branch.name}
                          secondary={`${branch.phone} | ${branch.address}`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton edge="end" aria-label="edit" onClick={() => openEditBranch(index)} sx={{ color: "#1976d2", mr: 1 }}>
                            <EditIcon size="small" />
                          </IconButton>
                          <IconButton edge="end" aria-label="delete" onClick={() => removeBranch(index)} sx={{ color: "#d32f2f" }}>
                            <DeleteIcon size="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < branches.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Box>
          )}
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: "#f8f9fa" }}>
        {isBranchFormOpen ? null : (
          <>
            <Button onClick={close} disabled={isSubmitting}>Cancel</Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              disabled={isSubmitting}
              sx={{ minWidth: 100 }}
            >
              {isSubmitting ? <CircularProgress size={24} color="inherit" /> : (editData ? "Update Shop" : "Save Shop")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
